import React, { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { BrandLogo } from '@/lib/brand';
import {
    Search, LogOut, Phone, User as UserIcon, Award, BookOpen, Wallet, Activity,
    Building2, MapPin, Calendar, CheckCircle2, XCircle, History, ArrowDownCircle,
} from 'lucide-react';

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString() : '—');

export default function CallCenterLookup() {
    const { user, logout } = useAuth();
    const [mobile, setMobile] = useState('');
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');

    const search = async (e) => {
        e.preventDefault();
        const m = mobile.trim();
        if (!m) return;
        setBusy(true); setErr(''); setData(null);
        try {
            const r = await api.get('/callcenter/lookup', { params: { mobile: m } });
            setData(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Lookup failed');
        } finally { setBusy(false); }
    };

    const p = data?.profile;
    const s = data?.summary;

    return (
        <div className="min-h-screen bg-slate-50" data-testid="callcenter-page">
            {/* Header */}
            <header className="bg-brand-black text-white">
                <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <BrandLogo dark />
                        <span className="hidden sm:inline text-xs font-bold tracking-[0.2em] uppercase text-brand-yellow">Call Centre · View only</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                        <span className="hidden sm:inline text-slate-300">{user?.email}</span>
                        <button onClick={logout} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white" data-testid="callcenter-logout"><LogOut className="h-4 w-4" /> Logout</button>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-5 py-8">
                <h1 className="font-display font-extrabold text-2xl lg:text-3xl text-slate-900">Learner Lookup</h1>
                <p className="text-slate-500 mt-1 text-sm">Enter a mobile number to view the learner's profile status and points passbook.</p>

                <form onSubmit={search} className="mt-5 flex gap-2 max-w-md" data-testid="callcenter-search-form">
                    <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile number" inputMode="numeric"
                            className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-300 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 text-sm" data-testid="callcenter-mobile-input" />
                    </div>
                    <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-3 rounded-xl bg-brand-blue text-white font-semibold disabled:opacity-60" data-testid="callcenter-search-btn">
                        <Search className="h-4 w-4" /> {busy ? 'Searching…' : 'Search'}
                    </button>
                </form>

                {err && <div className="mt-5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm max-w-md" data-testid="callcenter-error">{err}</div>}

                {data && (
                    <div className="mt-8 space-y-6" data-testid="callcenter-result">
                        {/* Profile status */}
                        <section className="bg-white rounded-2xl border border-slate-200 p-6">
                            <div className="flex items-start justify-between flex-wrap gap-4">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <div className="h-10 w-10 rounded-full bg-brand-yellow/20 flex items-center justify-center"><UserIcon className="h-5 w-5 text-brand-black" /></div>
                                        <div>
                                            <div className="font-display font-bold text-xl text-slate-900" data-testid="cc-profile-name">{p.full_name || '—'}</div>
                                            <div className="text-sm text-slate-500 font-mono" data-testid="cc-profile-mobile">{p.mobile}</div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    {p.is_active
                                        ? <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-semibold"><CheckCircle2 className="h-4 w-4" /> Active</span>
                                        : <span className="inline-flex items-center gap-1 text-rose-500 text-sm font-semibold"><XCircle className="h-4 w-4" /> Inactive</span>}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                                <Stat icon={Wallet} label="Points balance" value={s.current_balance} testid="cc-balance" accent />
                                <Stat icon={Activity} label="Lifetime earned" value={s.lifetime_earned} testid="cc-lifetime" />
                                <Stat icon={BookOpen} label="Courses completed" value={p.courses_completed} testid="cc-courses" />
                                <Stat icon={Award} label="Certificates" value={p.certificates} testid="cc-certs" />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mt-6 text-sm">
                                <Detail icon={Building2} label="Department" value={p.department || '—'} />
                                <Detail icon={MapPin} label="Region" value={p.region || '—'} />
                                <Detail icon={MapPin} label="Location" value={p.location || '—'} />
                                <Detail icon={UserIcon} label="Employee ID" value={p.employee_id || '—'} />
                                <Detail icon={Phone} label="Email" value={p.email || '—'} />
                                <Detail icon={Calendar} label="Member since" value={fmtDay(p.member_since)} />
                                <Detail icon={History} label="Last activity" value={fmtDate(p.last_activity_at)} />
                                <Detail icon={Activity} label="Current streak" value={`${p.streak_days || 0} days`} />
                            </div>
                        </section>

                        {/* Points passbook */}
                        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                            <div className="px-6 py-3 border-b border-slate-100 font-display font-bold text-slate-900 flex items-center gap-2"><Wallet className="h-5 w-5 text-brand-yellow" /> Points passbook <span className="text-xs font-normal text-slate-400">({s.passbook_count})</span></div>
                            {data.passbook.length === 0 ? <div className="p-6 text-slate-400 text-sm" data-testid="cc-passbook-empty">No points earned yet.</div> : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm" data-testid="cc-passbook-table">
                                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                                            <tr><th className="text-left px-6 py-2">Date</th><th className="text-left px-6 py-2">Description</th><th className="text-left px-6 py-2">Type</th><th className="text-left px-6 py-2">Transaction ID</th><th className="text-right px-6 py-2">Points</th></tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {data.passbook.map((r, i) => (
                                                <tr key={i} data-testid={`cc-passbook-row-${i}`}>
                                                    <td className="px-6 py-2.5 whitespace-nowrap text-slate-500 text-xs">{fmtDate(r.date)}</td>
                                                    <td className="px-6 py-2.5 text-slate-800">{r.description}</td>
                                                    <td className="px-6 py-2.5 text-slate-600">{r.type}</td>
                                                    <td className="px-6 py-2.5 font-mono text-xs text-slate-500">{r.txn_id || '—'}</td>
                                                    <td className="px-6 py-2.5 text-right font-bold text-emerald-600">+{r.points}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        {/* Adjustments */}
                        {data.adjustments.length > 0 && (
                            <section className="bg-white rounded-2xl border border-amber-200 overflow-hidden" data-testid="cc-adjustments">
                                <div className="px-6 py-3 border-b border-amber-100 font-display font-bold text-slate-900 flex items-center gap-2"><ArrowDownCircle className="h-5 w-5 text-amber-600" /> Adjustments &amp; reversals <span className="text-xs font-normal text-slate-400">({s.adjustments_count})</span></div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-amber-50/60 text-slate-500 text-xs uppercase">
                                            <tr><th className="text-left px-6 py-2">Date</th><th className="text-left px-6 py-2">Description</th><th className="text-left px-6 py-2">Type</th><th className="text-left px-6 py-2">Transaction ID</th><th className="text-right px-6 py-2">Points</th></tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {data.adjustments.map((r, i) => (
                                                <tr key={i} data-testid={`cc-adjustment-row-${i}`}>
                                                    <td className="px-6 py-2.5 whitespace-nowrap text-slate-500 text-xs">{fmtDate(r.date)}</td>
                                                    <td className="px-6 py-2.5 text-slate-800">{r.description}{r.original_points != null ? <span className="text-xs text-slate-400"> ({r.original_points}→{r.corrected_to})</span> : null}</td>
                                                    <td className="px-6 py-2.5 text-slate-600">{r.type}</td>
                                                    <td className="px-6 py-2.5 font-mono text-xs text-slate-500">{r.txn_id || '—'}</td>
                                                    <td className="px-6 py-2.5 text-right font-bold text-rose-600">{r.points}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

function Stat({ icon: Icon, label, value, testid, accent }) {
    return (
        <div className={`rounded-xl p-4 ${accent ? 'bg-brand-yellow/10 ring-1 ring-brand-yellow/40' : 'bg-slate-50'}`}>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide"><Icon className="h-3.5 w-3.5" /> {label}</div>
            <div className="mt-1 font-display font-extrabold text-2xl text-slate-900" data-testid={testid}>{value}</div>
        </div>
    );
}

function Detail({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-2">
            <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
            <div><span className="text-slate-500">{label}: </span><span className="font-semibold text-slate-800">{value}</span></div>
        </div>
    );
}
